/**
 * WebsiteHero — full-bleed hero with headline, subhead, dual CTAs and trust badges.
 */
import { Phone, ArrowRight, ShieldCheck } from 'lucide-react';

export default function WebsiteHero({ config }) {
  const hero = config.hero || {};
  const primary = config.brand?.primary || '#0f4c81';
  const primaryDark = config.brand?.primary_dark || '#0a3a64';

  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
      }}
      data-testid="ws-hero"
    >
      {/* subtle noise + dot grid */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          {hero.eyebrow && (
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-5">
              {hero.eyebrow}
            </p>
          )}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05]"
            data-testid="ws-hero-headline"
          >
            {hero.headline}
          </h1>
          {hero.subhead && (
            <p className="mt-6 text-base lg:text-lg text-white/85 max-w-2xl leading-relaxed">
              {hero.subhead}
            </p>
          )}

          <div className="mt-9 flex flex-col sm:flex-row gap-3" data-testid="ws-hero-ctas">
            {hero.primary_cta && (
              <a
                href={hero.primary_cta.href}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-900 font-bold px-7 py-3.5 rounded-full text-sm shadow-lg shadow-black/10 transition-all hover:shadow-xl"
                data-testid="ws-hero-primary-cta"
              >
                {hero.primary_cta.label} <ArrowRight size={16} />
              </a>
            )}
            {hero.secondary_cta && (
              <a
                href={hero.secondary_cta.href}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/30 px-7 py-3.5 rounded-full text-sm backdrop-blur-sm transition-all"
                data-testid="ws-hero-secondary-cta"
              >
                <Phone size={16} /> {hero.secondary_cta.label}
              </a>
            )}
          </div>

          {Array.isArray(hero.badges) && hero.badges.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3" data-testid="ws-hero-badges">
              {hero.badges.map((b) => (
                <div
                  key={b}
                  className="inline-flex items-center gap-1.5 text-[13px] text-white/85 font-medium"
                >
                  <ShieldCheck size={14} className="text-emerald-300" /> {b}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
